// CPU reference of the colour stage (§6.2 passes 3–10), mirroring the GLSL exactly, including the guide.
// Used to generate golden references (scripts/make-golden.ts) and in unit tests. Not used at runtime.
import { lumaP3, type Mat3 } from '../color/matrix';
import { wbMatrix } from '../color/wb';
import { bakeLut, isIdentity } from '../color/curve';
import { encodeSigned, decodeSigned, linearToSrgb } from '../color/transfer';
import { shoulder, toneY } from '../color/tone';
import { adjustLch, HUE_BANDS } from '../color/hsl';
import { p3ToOklab, oklabToP3, toLch, fromLch } from '../color/oklab';
import type { Settings } from '../model/types';

export type LinearImage = { width: number; height: number; data: Float32Array }; // linear P3 RGBA, straight alpha

/** Exact box average to (gw, gh) — the GPU's tap pattern reproduces this when the factor is even. */
function boxDown(img: LinearImage, gw: number, gh: number): Float32Array {
  const { width: W, height: H, data } = img;
  const out = new Float32Array(gw * gh * 4);
  for (let y = 0; y < gh; y++)
    for (let x = 0; x < gw; x++) {
      const x0 = (x * W) / gw,
        x1 = ((x + 1) * W) / gw,
        y0 = (y * H) / gh,
        y1 = ((y + 1) * H) / gh;
      let r = 0,
        g = 0,
        b = 0,
        a = 0,
        n = 0;
      for (let sy = Math.floor(y0); sy < Math.ceil(y1); sy++)
        for (let sx = Math.floor(x0); sx < Math.ceil(x1); sx++) {
          const i = (sy * W + sx) * 4;
          const al = data[i + 3]!;
          r += data[i]! * al;
          g += data[i + 1]! * al;
          b += data[i + 2]! * al;
          a += al;
          n++;
        }
      const o = (y * gw + x) * 4;
      out[o] = a ? r / a : 0;
      out[o + 1] = a ? g / a : 0;
      out[o + 2] = a ? b / a : 0;
      out[o + 3] = a / n;
    }
  return out;
}

function gaussian1d(
  src: Float32Array,
  w: number,
  h: number,
  sigma: number,
  horizontal: boolean,
): Float32Array {
  const r = Math.min(Math.ceil(sigma * 3), 32);
  const k: number[] = [];
  let sum = 0;
  for (let i = -r; i <= r; i++) {
    const v = Math.exp((-0.5 * i * i) / (sigma * sigma));
    k.push(v);
    sum += v;
  }
  const out = new Float32Array(src.length);
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      let acc = 0;
      for (let i = -r; i <= r; i++) {
        const sx = horizontal ? Math.min(w - 1, Math.max(0, x + i)) : x;
        const sy = horizontal ? y : Math.min(h - 1, Math.max(0, y + i));
        acc += k[i + r]! * src[sy * w + sx]!;
      }
      out[y * w + x] = acc / sum;
    }
  return out;
}

/** The guide: 1/8-res blurred luminance after WB and exposure (same construction as Renderer.guide). */
export function cpuGuide(
  img: LinearImage,
  s: Settings,
): { data: Float32Array; width: number; height: number } {
  const gw = Math.max(8, Math.round(img.width / 8));
  const gh = Math.max(8, Math.round(img.height / 8));
  const small = boxDown(img, gw, gh);
  const m = wbMatrix(s.wb.temp, s.wb.tint);
  const gain = 2 ** s.light.exposure;
  const Y = new Float32Array(gw * gh);
  for (let i = 0; i < gw * gh; i++) {
    const r = small[i * 4]!,
      g = small[i * 4 + 1]!,
      b = small[i * 4 + 2]!;
    const c = [0, 1, 2].map((k) => (m[k]![0] * r + m[k]![1] * g + m[k]![2] * b) * gain) as [
      number,
      number,
      number,
    ];
    Y[i] = Math.max(lumaP3(c[0], c[1], c[2]), 1e-6);
  }
  const sigma = 0.015 * Math.max(gw, gh);
  return {
    data: gaussian1d(gaussian1d(Y, gw, gh, sigma, true), gw, gh, sigma, false),
    width: gw,
    height: gh,
  };
}

/** Bilinear sample of a single-channel image at normalized (u, v), clamp-to-edge (GPU LINEAR filtering). */
function bilinear(img: { data: Float32Array; width: number; height: number }, u: number, v: number): number {
  const x = Math.min(img.width - 1, Math.max(0, u * img.width - 0.5));
  const y = Math.min(img.height - 1, Math.max(0, v * img.height - 0.5));
  const x0 = Math.floor(x),
    y0 = Math.floor(y);
  const x1 = Math.min(img.width - 1, x0 + 1),
    y1 = Math.min(img.height - 1, y0 + 1);
  const fx = x - x0,
    fy = y - y0;
  const d = img.data,
    w = img.width;
  return (
    (d[y0 * w + x0]! * (1 - fx) + d[y0 * w + x1]! * fx) * (1 - fy) +
    (d[y1 * w + x0]! * (1 - fx) + d[y1 * w + x1]! * fx) * fy
  );
}

const applyM = (m: Mat3, r: number, g: number, b: number): [number, number, number] => [
  m[0][0] * r + m[0][1] * g + m[0][2] * b,
  m[1][0] * r + m[1][1] * g + m[1][2] * b,
  m[2][0] * r + m[2][1] * g + m[2][2] * b,
];

/** Colour stage on a whole image; returns linear P3 RGBA (straight alpha). Geometry is not applied. */
export function cpuColor(img: LinearImage, s: Settings): LinearImage {
  const { width: W, height: H, data } = img;
  const guide = cpuGuide(img, s);
  const wb = wbMatrix(s.wb.temp, s.wb.tint);
  const doWb = s.wb.temp !== 0 || s.wb.tint !== 0;
  const gain = 2 ** s.light.exposure;
  const L = s.light;
  const doTone = L.highlights !== 0 || L.shadows !== 0 || L.contrast !== 0;
  const doWbk = L.whites !== 0 || L.blacks !== 0;
  const doCurve = !(
    isIdentity(s.curve.master) &&
    isIdentity(s.curve.r) &&
    isIdentity(s.curve.g) &&
    isIdentity(s.curve.b)
  );
  const luts = doCurve ? [s.curve.master, s.curve.r, s.curve.g, s.curve.b].map((p) => bakeLut(p)) : [];
  const curveAt = (v: number, row: number) => {
    // Linear interpolation between LUT entries, like the GPU's LINEAR sampling of the 1024-wide texture.
    const x = Math.min(1, Math.max(0, v)) * 1023;
    const i = Math.floor(x),
      f = x - i;
    const lut = luts[row]!;
    return lut[i]! * (1 - f) + lut[Math.min(1023, i + 1)]! * f;
  };
  const doHsl = HUE_BANDS.some((b) => s.hsl[b].h !== 0 || s.hsl[b].s !== 0 || s.hsl[b].l !== 0);
  const doColor = doHsl || s.color.vibrance !== 0 || s.color.saturation !== 0;
  const out = new Float32Array(data.length);
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      const i = (y * W + x) * 4;
      let c: [number, number, number] = [data[i]!, data[i + 1]!, data[i + 2]!];
      if (doWb) c = applyM(wb, c[0], c[1], c[2]);
      if (L.exposure !== 0) c = [c[0] * gain, c[1] * gain, c[2] * gain];
      if (doTone) {
        const Y = Math.max(lumaP3(c[0], c[1], c[2]), 1e-6);
        const Y2 = toneY(Y, bilinear(guide, (x + 0.5) / W, (y + 0.5) / H), L);
        const k = Y2 / Y;
        c = [c[0] * k, c[1] * k, c[2] * k];
      }
      c = [shoulder(c[0]), shoulder(c[1]), shoulder(c[2])];
      if (doWbk || doCurve) {
        let v = c.map(encodeSigned) as [number, number, number];
        if (doWbk)
          v = v.map((e) => e + (L.whites / 100) * 0.2 * Math.max(e, 0) ** 4) as [number, number, number];
        if (doWbk)
          v = v.map((e) => e + (L.blacks / 100) * 0.12 * Math.max(1 - e, 0) ** 4) as [number, number, number];
        v = v.map((e) => Math.min(1, Math.max(0, e))) as [number, number, number];
        if (doCurve) {
          v = [curveAt(v[0], 0), curveAt(v[1], 0), curveAt(v[2], 0)];
          v = [curveAt(v[0], 1), curveAt(v[1], 2), curveAt(v[2], 3)];
        }
        c = v.map(decodeSigned) as [number, number, number];
      }
      if (doColor) {
        const lch = adjustLch(toLch(p3ToOklab(c)), doHsl ? s.hsl : null, s.color);
        c = oklabToP3(fromLch(lch));
      }
      out[i] = c[0];
      out[i + 1] = c[1];
      out[i + 2] = c[2];
      out[i + 3] = data[i + 3]!;
    }
  return { width: W, height: H, data: out };
}

// ── Detail (§6.6), mirroring shaders/detailPasses.ts ──────────────────────────
const smooth = (e0: number, e1: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};
export const nrRangeSigma = (nrLuma: number) => 0.002 + (nrLuma / 100) * 0.08;
export const maskThresholds = (masking: number): [number, number] => {
  const m = masking / 100;
  return [0.08 * m, 0.08 * m + 0.04];
};

/** Detail on a linear P3 image: noise reduction (OkLab), then unsharp mask on encoded luma. `scale` shrinks
 *  the spatial radii (the preview at less than 100%). */
export function cpuDetail(img: LinearImage, d: Settings['detail'], scale = 1): LinearImage {
  const { width: W, height: H } = img;
  let data = img.data;
  const at = (x: number, y: number) =>
    (Math.min(H - 1, Math.max(0, y)) * W + Math.min(W - 1, Math.max(0, x))) * 4;
  if (d.nrLuma > 0 || d.nrColor > 0) {
    const lab = new Float32Array(W * H * 3);
    for (let p = 0; p < W * H; p++)
      lab.set(p3ToOklab([data[p * 4]!, data[p * 4 + 1]!, data[p * 4 + 2]!]), p * 3);
    const sr = nrRangeSigma(d.nrLuma);
    const ss = 1.5 * scale,
      sa = 2 * scale;
    const out = new Float32Array(data.length);
    for (let y = 0; y < H; y++)
      for (let x = 0; x < W; x++) {
        const c = (y * W + x) * 3;
        const L0 = lab[c]!;
        let wsum = 0,
          Lacc = 0,
          gsum = 0,
          aAcc = 0,
          bAcc = 0;
        for (let j = -4; j <= 4; j++)
          for (let i = -4; i <= 4; i++) {
            const q = (at(x + i, y + j) / 4) * 3;
            const g = Math.exp(-(i * i + j * j) / (2 * sa * sa));
            gsum += g;
            aAcc += g * lab[q + 1]!;
            bAcc += g * lab[q + 2]!;
            if (Math.abs(i) <= 3 && Math.abs(j) <= 3) {
              const dl = lab[q]! - L0;
              const w = Math.exp(-(i * i + j * j) / (2 * ss * ss)) * Math.exp(-(dl * dl) / (2 * sr * sr));
              wsum += w;
              Lacc += w * lab[q]!;
            }
          }
        const L = d.nrLuma > 0 ? Lacc / wsum : L0;
        const k = d.nrColor / 100;
        const a = lab[c + 1]! + (aAcc / gsum - lab[c + 1]!) * k;
        const b = lab[c + 2]! + (bAcc / gsum - lab[c + 2]!) * k;
        out.set([...oklabToP3([L, a, b]), data[(y * W + x) * 4 + 3]!], (y * W + x) * 4);
      }
    data = out;
  }
  if (d.sharpAmount > 0) {
    const enc = new Float32Array(W * H * 3);
    const v = new Float32Array(W * H);
    for (let p = 0; p < W * H; p++) {
      const e = [encodeSigned(data[p * 4]!), encodeSigned(data[p * 4 + 1]!), encodeSigned(data[p * 4 + 2]!)];
      enc.set(e, p * 3);
      v[p] = lumaP3(e[0]!, e[1]!, e[2]!);
    }
    const sigma = Math.max(0.3, d.sharpRadius * scale);
    const r = Math.min(Math.ceil(sigma * 3), 12);
    const k: number[] = [];
    let ks = 0;
    for (let i = -r; i <= r; i++) {
      const w = Math.exp((-0.5 * i * i) / (sigma * sigma));
      k.push(w);
      ks += w;
    }
    const tmp = new Float32Array(W * H),
      blur = new Float32Array(W * H);
    for (let y = 0; y < H; y++)
      for (let x = 0; x < W; x++) {
        let acc = 0;
        for (let i = -r; i <= r; i++) acc += k[i + r]! * v[y * W + Math.min(W - 1, Math.max(0, x + i))]!;
        tmp[y * W + x] = acc / ks;
      }
    for (let y = 0; y < H; y++)
      for (let x = 0; x < W; x++) {
        let acc = 0;
        for (let i = -r; i <= r; i++) acc += k[i + r]! * tmp[Math.min(H - 1, Math.max(0, y + i)) * W + x]!;
        blur[y * W + x] = acc / ks;
      }
    const [t0, t1] = maskThresholds(d.sharpMasking);
    const vv = (x: number, y: number) =>
      v[Math.min(H - 1, Math.max(0, y)) * W + Math.min(W - 1, Math.max(0, x))]!;
    const out = new Float32Array(data.length);
    for (let y = 0; y < H; y++)
      for (let x = 0; x < W; x++) {
        const p = y * W + x;
        let mask = 1;
        if (d.sharpMasking > 0) {
          const gx =
            (vv(x + 1, y - 1) +
              2 * vv(x + 1, y) +
              vv(x + 1, y + 1) -
              vv(x - 1, y - 1) -
              2 * vv(x - 1, y) -
              vv(x - 1, y + 1)) /
            8;
          const gy =
            (vv(x - 1, y + 1) +
              2 * vv(x, y + 1) +
              vv(x + 1, y + 1) -
              vv(x - 1, y - 1) -
              2 * vv(x, y - 1) -
              vv(x + 1, y - 1)) /
            8;
          mask = smooth(t0, t1, Math.hypot(gx, gy));
        }
        const delta = (d.sharpAmount / 100) * (v[p]! - blur[p]!) * mask;
        for (let c = 0; c < 3; c++) out[p * 4 + c] = decodeSigned(enc[p * 3 + c]! + delta);
        out[p * 4 + 3] = data[p * 4 + 3]!;
      }
    data = out;
  }
  return { width: W, height: H, data };
}

/**
 * §6.5 gamut mapping at output: a colour outside the output gamut keeps its OkLab lightness and hue and
 * loses chroma (binary search, 12 steps) until it fits. `toOut` = working → linear output space.
 */
export function gamutMap(c: [number, number, number], toOut: Mat3): [number, number, number] {
  const o = applyM(toOut, c[0], c[1], c[2]);
  const eps = 1e-4;
  if (o.every((v) => v >= -eps && v <= 1 + eps)) return o;
  const lch = toLch(p3ToOklab(c));
  const L = Math.min(1, Math.max(0, lch[0]));
  let lo = 0,
    hi = 1;
  for (let i = 0; i < 12; i++) {
    const mid = (lo + hi) / 2;
    const q = applyM(toOut, ...oklabToP3(fromLch([L, lch[1] * mid, lch[2]])));
    if (q.every((v) => v >= -eps && v <= 1 + eps)) lo = mid;
    else hi = mid;
  }
  return applyM(toOut, ...oklabToP3(fromLch([L, lch[1] * lo, lch[2]])));
}

/** Linear P3 → encoded P3 (sRGB curve) with gamut mapping and clamping — what the output pass writes for 'p3'. */
export function encodeP3(img: LinearImage): Float32Array {
  const out = new Float32Array(img.data.length);
  const I: Mat3 = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ];
  for (let i = 0; i < img.data.length; i += 4) {
    const g = gamutMap([img.data[i]!, img.data[i + 1]!, img.data[i + 2]!], I);
    for (let c = 0; c < 3; c++) out[i + c] = linearToSrgb(Math.min(1, Math.max(0, g[c]!)));
    out[i + 3] = img.data[i + 3]!;
  }
  return out;
}
